"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import { BinomialTestResults } from "@/components/tools/stats-tests-more/binomial-test-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import { binomialTest } from "@/lib/tool-kit/stats-tests-more/binomial-test";

/** English strings. A translated content file overrides these through `ui`. */
export const BINOMIAL_TEST_CALCULATOR_STRINGS = {
  successesLabel: "Number of successes (k)",
  trialsLabel: "Number of trials (n)",
  p0Label: "Hypothesised probability of success (p₀)",
  p0Hint: "A value between 0 and 1, such as 0.5 or 0.25.",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: the probability is not p₀",
  less: "The probability is less than p₀",
  greater: "The probability is greater than p₀",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level for the interval",
  tryExample: "Try an example",
  exampleSuccesses: "61",
  exampleTrials: "150",
  exampleP0: "0.3",
  results: "Results",
  pValue: "p-value (exact)",
  proportion: "Observed proportion (p̂)",
  counts: "Successes / trials",
  ciLabel: "Clopper–Pearson {level} CI",
  cohenH: "Cohen's h",
  expected: "Expected successes under H₀",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the true probability differs from {p0}.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the probability differs from {p0}.",
  pValuesHeading: "All three p-values",
  alternative: "Alternative",
  p: "p",
  intervalsHeading: "Confidence intervals for the true probability",
  clopper: "Clopper–Pearson (exact): [{lo}, {hi}]",
  clopperOneSided:
    "Clopper–Pearson (exact, one-sided): [{lo}, {hi}] — the bound is at the full confidence level, as R reports it.",
  wilson: "Wilson score (approximate), two-sided: [{lo}, {hi}]",
  intervalHint:
    "The exact interval never undercovers, so it is slightly wider than Wilson's; both exclude p₀ when the test rejects, usually.",
  tableHeading: "The binomial distribution under H₀",
  tableHint:
    "Outcomes marked Yes are no more likely than the one you observed ({k}); their probabilities add up to the two-sided p-value.",
  regionPrefix: "Outcomes counted in the two-sided p-value: {parts}.",
  regionLow: "k ≤ {low} (probability {p})",
  regionHigh: "k ≥ {high} (probability {p})",
  regionJoin: "{a} and {b}",
  outcome: "k",
  pmf: "P(X = k)",
  lower: "P(X ≤ k)",
  upper: "P(X ≥ k)",
  bar: "Probability",
  counted: "In two-sided p",
  yes: "Yes",
  no: "No",
  observed: "observed",
  howCalculated: "How this was calculated",
  workingLess: "p (less) = P(X ≤ {k}) = {p}, with X ~ Binomial({n}, {p0}).",
  workingGreater:
    "p (greater) = P(X ≥ {k}) = {p}, with X ~ Binomial({n}, {p0}).",
  workingTwo:
    "p (two-sided) = P(X ≤ {low}) + P(X ≥ {high}) = {p}: all outcomes with probability ≤ P(X = {k}) = {pk}.",
  workingTwoAll:
    "p (two-sided) = {p}: no outcome is less likely than the one observed.",
  smallExpected:
    "The exact test is valid whatever the counts; normal-approximation tests need at least 10 expected successes and failures, which the exact test does not.",
  apaHeading: "APA-style result",
  apaTemplate:
    "{k} of {n} (*p̂* = {phat}), exact binomial test against *p*₀ = {p0}: *p* {p}, {level} CI [{lo}, {hi}]",
  apaNote:
    "Name the test (exact binomial test) and the hypothesised probability, and report the interval.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter the number of successes, the number of trials, and p₀.",
  errorCounts:
    "The number of successes must be a whole number from 0 up to the number of trials.",
  errorP0: "p₀ must be strictly between 0 and 1.",
} as const;

export default function BinomialTestCalculatorWidget() {
  const strings = useToolUi(BINOMIAL_TEST_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [successes, setSuccesses] = useState("");
  const [trials, setTrials] = useState("");
  const [p0Text, setP0Text] = useState("0.5");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const k = parseIntegerInput(successes);
  const n = parseIntegerInput(trials);
  const p0 = parseNumberInput(p0Text);
  const outcome =
    k !== null && n !== null && p0 !== null
      ? binomialTest(k, n, p0, { alternative, level: Number(level) })
      : null;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  let notice: string = strings.prompt;
  if (outcome && !outcome.ok) {
    notice =
      outcome.reason === "invalid-probability"
        ? strings.errorP0
        : strings.errorCounts;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="bn-successes"
          label={strings.successesLabel}
          numeric
          onChange={setSuccesses}
          value={successes}
        />
        <TextField
          id="bn-trials"
          label={strings.trialsLabel}
          numeric
          onChange={setTrials}
          value={trials}
        />
        <TextField
          hint={strings.p0Hint}
          id="bn-p0"
          label={strings.p0Label}
          numeric
          onChange={setP0Text}
          value={p0Text}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="bn-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="bn-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="bn-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton
        label={strings.tryExample}
        onClick={() => {
          setSuccesses(strings.exampleSuccesses);
          setTrials(strings.exampleTrials);
          setP0Text(strings.exampleP0);
        }}
      />
      {outcome?.ok ? (
        <BinomialTestResults
          alpha={Number(alpha)}
          fmt={fmt}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
